<template>
  <div>
    <q-expansion-item
      label="Search Filters"
      color="primary"
      switch-toggle-side
      expand-separator
      class="q-mb-md"
      :dense="true"
      expand-icon="expand_more"
      expand-icon-class="text-primary"
    >
      <div class="q-pa-md">
        <!-- Dropdown Filters Row -->
        <div class="row q-col-gutter-md q-mb-md">
          <!-- Customer Dropdown -->
          <div class="col-4">
            <q-select
              v-model="filters.customer_id"
              :options="customerOptions"
              label="Customer"
              outlined
              dense
            />
          </div>

          <!-- Producer Dropdown -->
          <div class="col-4">
            <q-select
              v-model="filters.producer_id"
              :options="producerOptions"
              label="Producer"
              outlined
              dense
            />
          </div>

          <!-- Hauler Dropdown -->
          <div class="col-4">
            <q-select
              v-model="filters.hauler_id"
              :options="haulerOptions"
              label="Hauler"
              outlined
              dense
            />
          </div>
        </div>

        <!-- Date/Ticket -->
        <div class="row q-col-gutter-md q-mb-md">
          <div class="col-4">
            <q-select
              v-model="filters.field_id"
              :options="fieldOptions"
              label="Field"
              multiple
              use-chips
              outlined
              dense
            />
          </div>
          <div class="col-4">
            <q-select
              v-model="filters.crop_id"
              :options="cropOptions"
              label="Crop"
              outlined
              dense
            />
          </div>
          <div class="col-4">
            <q-input
              v-model="filters.ticketNumber"
              label="Ticket Number"
              outlined
              dense
              debounce="300"
            />
          </div>
        </div>

        <div class="row q-col-gutter-md q-mb-md">
          <div class="col-4">
            <q-input
              v-model="filters.dateStart"
              label="Start Date"
              outlined
              dense
              readonly
              clearable
              @clear="clearDateStart"
            >
              <template v-slot:prepend>
                <q-icon name="event" color="primary" />
              </template>

              <template v-slot:append>
                <q-icon
                  name="arrow_drop_down"
                  color="primary"
                  class="cursor-pointer"
                  aria-label="Open start date picker"
                >
                  <q-popup-proxy transition-show="scale" transition-hide="scale">
                    <q-date
                      v-model="filters.dateStart"
                      :options="restrictDates"
                      mask="YYYY-MM-DD"
                      class="q-pa-md"
                    >
                      <div class="row items-center justify-end q-gutter-sm">
                        <q-btn label="Clear" color="primary" flat @click="clearDateStart" />
                      </div>
                    </q-date>
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
          </div>

          <div class="col-4">
            <q-input
              v-model="filters.dateEnd"
              label="End Date"
              outlined
              dense
              readonly
              clearable
              @clear="clearDateEnd"
            >
              <template v-slot:prepend>
                <q-icon name="event" color="primary" />
              </template>

              <template v-slot:append>
                <q-icon
                  name="arrow_drop_down"
                  color="primary"
                  class="cursor-pointer"
                  aria-label="Open end date picker"
                >
                  <q-popup-proxy transition-show="scale" transition-hide="scale">
                    <q-date
                      v-model="filters.dateEnd"
                      :options="restrictDates"
                      mask="YYYY-MM-DD"
                      class="q-pa-md"
                    >
                      <div class="row items-center justify-end q-gutter-sm">
                        <q-btn label="Clear" color="primary" flat @click="clearDateEnd" />
                      </div>
                    </q-date>
                  </q-popup-proxy>
                </q-icon>
              </template>
            </q-input>
          </div>

          <div class="col-4">
            <q-select
              v-model="filters.bag_pit_id"
              :options="customerBagPitOptions"
              label="Customer Bag Pit"
              outlined
              dense
              clearable
            />
          </div>
        </div>

        <q-btn label="Reset"
          type="reset"
          @click="clearFilters"
          color="primary"
          flat
          class="q-mt-md">
        </q-btn>
      </div>
    </q-expansion-item>
    <!-- Collapsible Search/Filter Section -->
    <div class="q-mt-md">
      <div class="row q-gutter-sm q-ml-md">
        <div class="inline-tooltip-target">
          <q-btn
            color="primary"
            label="Edit Selected"
            @click="openModal"
            class="q-mb-md"
            :disable="editSelectedDisabled"
            :class="{ 'q-opacity-50': editSelectedDisabled }"
          />
          <q-tooltip v-if="editSelectedDisabled">
            {{ editSelectedDisabledMessage }}
          </q-tooltip>
        </div>
        <q-btn
          color="red"
          label="Delete Selected"
          @click="openDeleteModal"
          class="q-mb-md"
          :disable="selectedRows.length === 0"
          :class="{ 'q-opacity-50': selectedRows.length === 0 }"
        />
        <q-btn
          color="primary"
          outline
          :label="allCurrentPageSelected ? 'Deselect All' : 'Select All'"
          @click="toggleSelectAll"
          class="q-mb-md"
          :disable="currentPageTicketIds.length === 0"
        />
        <q-btn
          color="positive"
          outline
          label="Export CSV"
          @click="exportCsv"
          class="q-mb-md"
          :loading="exportingCsv"
          :disable="exportingCsv || totalTicketCrops === 0"
        />
      </div>
    </div>

    <BaseTable
      :rows="formattedTicketCrops"
      :columns="columns"
      :total-rows="totalTicketCrops"
      search-placeholder="Search Current Table..."
      @visible-rows-change="onVisibleRowsChange"
    >
      <template #actions="{ row }">
        <q-td class="">
          <q-checkbox class="q-mr-sm"
            v-model="selectedRows"
            :val="row.id"
            dense
          />
        </q-td>
      </template>
    </BaseTable>

    <!-- Pagination Control (bottom of table) -->
    <div class="row justify-left q-my-md q-ml-lg">
      <q-pagination
        v-model="currentPage"
        :max="totalPages"
        @update:model-value="onPageChange"
        color="primary"
        direction-links
        :max-pages="10"
      />
    </div>

    <!-- Modal for Editing Selected Rows -->
    <q-dialog v-model="showModal">
      <q-card class="q-pa-md">
        <q-card-section>
          <div class="text-h6">Editing {{ bulkEditSelectedRows.length }} Tickets</div>
        </q-card-section>

        <q-form ref="editFormRef" @submit.prevent="submitEditForm">
          <q-card-section>
            <div
              v-for="field in editFieldConfigs"
              :key="field.key"
              class="row items-center q-col-gutter-sm q-mb-sm"
            >
              <div class="col-auto">
                <q-checkbox
                  v-model="editFieldSelection[field.key]"
                  :label="field.label"
                  :disable="field.singleTicketOnly && bulkEditSelectedRows.length !== 1"
                  dense
                  @update:model-value="onEditFieldSelectionChange(field.key, $event)"
                />
              </div>
              <div class="col">
                <q-input
                  v-if="field.key === 'date'"
                  v-model="editForm.date"
                  :label="field.label"
                  :disable="!editFieldSelection.date"
                  :rules="[validateOptionalDate]"
                  readonly
                  outlined
                  dense
                >
                  <template v-slot:prepend>
                    <q-icon name="event" color="primary" />
                  </template>
                  <template v-slot:append>
                    <q-icon
                      name="arrow_drop_down"
                      color="primary"
                      class="cursor-pointer"
                      aria-label="Open date picker"
                    >
                      <q-popup-proxy
                        v-model="showBulkEditDatePicker"
                        transition-show="scale"
                        transition-hide="scale"
                      >
                        <q-date
                          v-model="editForm.date"
                          :options="restrictDates"
                          mask="YYYY-MM-DD"
                          class="q-pa-md"
                        />
                      </q-popup-proxy>
                    </q-icon>
                  </template>
                </q-input>
                <q-input
                  v-else-if="field.key === 'ticket_number'"
                  v-model="editForm.ticket_number"
                  :label="field.label"
                  :disable="!editFieldSelection.ticket_number"
                  :rules="[value => validateOptionalText(field.key, value)]"
                  outlined
                  dense
                />
                <q-select
                  v-else-if="isBulkEditSelection(field.key)"
                  v-model="editForm[field.key]"
                  :options="bulkEditOptions(field.key)"
                  :label="field.label"
                  :disable="!editFieldSelection[field.key]"
                  :rules="[value => validateOptionalSelection(field.key, value)]"
                  emit-value
                  map-options
                  outlined
                  dense
                />
                <q-input
                  v-else
                  v-model.number="editForm[field.key]"
                  :label="field.label"
                  :disable="!editFieldSelection[field.key]"
                  :rules="[value => validateOptionalNumeric(field.key, value)]"
                  outlined
                  dense
                />
              </div>
            </div>
          </q-card-section>

          <q-card-actions align="right">
            <q-btn label="Cancel" color="negative" @click="showModal = false" flat />
            <q-btn label="Submit" type="submit" color="primary" />
          </q-card-actions>
        </q-form>
      </q-card>
    </q-dialog>
    <q-dialog v-model="deleteModalVisible">
      <q-card>
        <q-card-section>
          <div class="text-h6">
            Confirm Deletion
          </div>
        </q-card-section>

        <q-card-section>
          <p>Are you sure you want to delete <b>{{ selectedRows.length }}</b> ticket(s)?</p>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Cancel" color="primary" @click="cancelBulkDelete" />
          <q-btn flat label="Delete" color="negative" @click="confirmBulkDelete" />
        </q-card-actions>
      </q-card>
    </q-dialog>

  </div>
</template>

<script>
import axios from 'axios';
import { watch } from 'vue';
import BaseTable from '@/components/BaseTable';
import { selectedYear } from '@/store/store';

export default {
  name: 'TicketCropTable',
  components: {
    BaseTable
  },
  data() {
    const query_params = new URLSearchParams(window.location.search);
    const initialJobId = query_params.get('job_id') || null;

    return {
      // Table Data
      ticketCrops: [],
      // Totals
      totalTicketCrops: 0,
      ticketFetchSequence: 0,

      // Pagination State
      currentPage: 1,
      pageSize: 350,
      totalPages: 1,
      exportingCsv: false,

      // Selections / Modals
      selectedRows: [],
      visibleRowIds: null,
      showModal: false,
      deleteModalVisible: false,
      showBulkEditDatePicker: false,
      initialJobId,

      // Form data
      editForm: {
        date: null,
        at_moisture: null,
        moisture: null,
        customer_rate: null,
        producer_rate: null,
        haul_rate: null,
        haul_mile_rate: null,
        haul_mile: null,
        min_mile: null,
        bag_pit_id: null,
        truck_id: null,
        customer_id: null,
        producer_id: null,
        ticket_number: null,
        ids: []
      },
      editFieldSelection: {
        date: false,
        at_moisture: false,
        moisture: false,
        customer_rate: false,
        producer_rate: false,
        haul_rate: false,
        haul_mile_rate: false,
        haul_mile: false,
        min_mile: false,
        bag_pit_id: false,
        truck_id: false,
        customer_id: false,
        producer_id: false,
        ticket_number: false
      },
      editFieldConfigs: [
        { key: 'date', label: 'Date' },
        { key: 'at_moisture', label: 'At Moisture' },
        { key: 'moisture', label: 'Moisture' },
        { key: 'customer_rate', label: 'Customer Rate' },
        { key: 'producer_rate', label: 'Producer Rate' },
        { key: 'haul_rate', label: 'Haul Rate' },
        { key: 'haul_mile_rate', label: 'Haul Mile Rate' },
        { key: 'haul_mile', label: 'Haul Mile' },
        { key: 'min_mile', label: 'Min Mile' },
        { key: 'bag_pit_id', label: 'Bag Pit' },
        { key: 'truck_id', label: 'Truck' },
        { key: 'customer_id', label: 'Customer' },
        { key: 'producer_id', label: 'Producer' },
        { key: 'ticket_number', label: 'Ticket Number', singleTicketOnly: true }
      ],

      // Filters
      filters: {
        ticketNumber: null,
        dateStart: null,
        dateEnd: null,
        customer_id: null,
        producer_id: null,
        hauler_id: null,
        field_id: [],
        crop_id: null,
        bag_pit_id: null,
        job_id: initialJobId,
      },

      // Dropdown options
      customerOptions: [],
      producerOptions: [],
      haulerOptions: [],
      fieldOptions: [],
      cropOptions: [],
      customerBagPitFilters: [],
      truckOptions: [],

      // Table columns
      columns: [
        { name: 'actions', align: 'center', label: '', field: 'actions', sortable: false },
        { name: 'ticket_number', align: 'center', label: 'Ticket Number', field: 'ticket_number', sortable: true },
        { name: 'date', align: 'left', label: 'Date', field: 'date', sortable: true },
        { name: 'field', align: 'left', label: 'Field', field: 'field', sortable: true },
        { name: 'hauler_id', align: 'left', label: 'Hauler', field: 'hauler_id', sortable: true },
        { name: 'truck_id', align: 'left', label: 'Truck', field: 'truck_id', sortable: true },
        { name: 'customer_id', align: 'left', label: 'Customer', field: 'customer_id', sortable: true },
        { name: 'producer_id', align: 'left', label: 'Producer', field: 'producer_id', sortable: true },
        { name: 'crop', align: 'left', label: 'Crop', field: 'crop', sortable: true },
        { name: 'gross_weight', align: 'left', label: 'Gross Weight', field: 'gross_weight', sortable: true },
        { name: 'tare_weight', align: 'left', label: 'Tare Weight', field: 'tare_weight', sortable: true },
        { name: 'at_moisture', align: 'left', label: 'At Moisture', field: 'at_moisture', sortable: true },
        { name: 'moisture', align: 'left', label: 'Moisture', field: 'moisture', sortable: true },
        { name: 'customer_rate', align: 'left', label: 'Customer Rate', field: 'customer_rate', sortable: true },
        { name: 'producer_rate', align: 'left', label: 'Producer Rate', field: 'producer_rate', sortable: true },
        { name: 'haul_rate', align: 'left', label: 'Haul Rate', field: 'haul_rate', sortable: true },
        { name: 'haul_mile_rate', align: 'left', label: 'Haul Mile Rate', field: 'haul_mile_rate', sortable: true },
        { name: 'haul_mile', align: 'left', label: 'Haul Mile', field: 'haul_mile', sortable: true },
        { name: 'min_mile', align: 'left', label: 'Min Mile', field: 'min_mile', sortable: true },
        { name: 'edit_date', align: 'left', label: 'Edit Date', field: 'edit_date', sortable: true }
      ]
    };
  },
  setup() {
    // Restrict date selection to the selectedYear
    const restrictDates = (date) => {
      let year = selectedYear.value;
      const start = `${year}/01/01`;
      const end = `${year}/12/31`;
      return date >= start && date <= end;
    };
    return {
      restrictDates
    };
  },
  computed: {
    formattedTicketCrops() {
      return this.ticketCrops.map(ticket => ({
        ...ticket,
        job_id: ticket.job_id?.id || 'N/A',
        truck_id: ticket.truck_id?.name || 'N/A',
        hauler_id: ticket.truck_id?.hauler_id?.name || 'N/A',
        customer_id: ticket.customer_id?.name || 'N/A',
        producer_id: ticket.producer_id?.name || 'N/A',
        crop: ticket.job_id?.crop_id?.crop || 'N/A',
        field: ticket.job_id?.field_id?.name || 'N/A',
        date: this.formatDisplayDate(ticket.date),
        edit_date: this.formatDisplayDate(ticket.edit_date)
      }));
    },
    currentPageTicketIds() {
      if (Array.isArray(this.visibleRowIds)) {
        return this.visibleRowIds;
      }

      return this.ticketCrops
        .map(ticket => ticket.id)
        .filter(id => id !== undefined && id !== null);
    },
    bulkEditSelectedRows() {
      const visibleIds = new Set(this.currentPageTicketIds);
      return this.selectedRows.filter(id => visibleIds.has(id));
    },
    allCurrentPageSelected() {
      return this.currentPageTicketIds.length > 0
        && this.currentPageTicketIds.every(id => this.selectedRows.includes(id));
    },
    normalizedDateRange() {
      const start = this.filters.dateStart || null;
      const end = this.filters.dateEnd || null;

      if (start && end && start > end) {
        return {
          start: end,
          end: start,
        };
      }

      return {
        start,
        end,
      };
    },
    hasEditFilterRestrictionSatisfied() {
      return !!this.filters.ticketNumber
        || this.filters.field_id.length > 0
        || (!!this.filters.producer_id && !!this.filters.crop_id);
    },
    editSelectedDisabled() {
      if (this.bulkEditSelectedRows.length === 0) {
        return true;
      }

      if (this.bulkEditSelectedRows.length === 1) {
        return false;
      }

      return !this.hasEditFilterRestrictionSatisfied;
    },
    editSelectedDisabledMessage() {
      if (this.bulkEditSelectedRows.length === 0) {
        return 'Select at least one visible ticket to enable bulk edit.';
      }

      if (this.bulkEditSelectedRows.length === 1) {
        return '';
      }

      return 'Enter a Ticket Number, select Field(s), or select both Producer and Crop to enable bulk edit.';
    },
    customerBagPitOptions() {
      if (!this.filters.customer_id) {
        return this.customerBagPitFilters;
      }

      return this.customerBagPitFilters.filter(
        option => option.customerId === this.filters.customer_id.value
      );
    }
  },
  watch: {
    'filters.customer_id'() {
      this.syncCustomerBagPitFilter();
    },
    filters: {
      deep: true,
      handler() {
        this.clearSelection();
        this.currentPage = 1;
        this.fetchTicketCrops();
      }
    }
  },
  created() {
    // Fetch data, filters, and watch year changes
    this.fetchTicketCrops();
    this.fetchFilterOptions();

    watch(selectedYear, () => {
      // Reset page to 1 if the year changes
      this.clearSelection();
      this.currentPage = 1;
      this.fetchFilterOptions();
      this.fetchTicketCrops();
    });
  },
  methods: {
    buildFilterParams() {
      const { start, end } = this.normalizedDateRange;

      return {
        page: this.currentPage,
        page_size: this.pageSize,
        year: selectedYear.value,
        ...(this.filters.ticketNumber && { ticket_number: this.filters.ticketNumber }),
        ...(start && { date_start: this.formatFilterDateTime(start, 'start') }),
        ...(end && { date_end: this.formatFilterDateTime(end, 'end') }),
        ...(this.filters.customer_id && { customer_id: this.filters.customer_id.value }),
        ...(this.filters.producer_id && { producer_id: this.filters.producer_id.value }),
        ...(this.filters.hauler_id && { hauler_id: this.filters.hauler_id.value }),
        ...(this.filters.field_id.length && {
          field_ids: this.filters.field_id.map(item => item.value).join(',')
        }),
        ...(this.filters.crop_id && { crop_id: this.filters.crop_id.value }),
        ...(this.filters.bag_pit_id && { bag_pit_id: this.filters.bag_pit_id.value }),
        ...(this.filters.job_id && { job_id: this.filters.job_id }),
      };
    },
    async fetchFilterOptions() {
      try {
        const [customers, producers, haulers, fields, crops, customerBagPits, trucks] = await Promise.all([
          axios.get('/api/v1/customer/customers/'),
          axios.get('/api/v1/producer/producers/'),
          axios.get('/api/v1/hauler/haulers/'),
          axios.get('/api/v1/ticket_crop/ticket-crop/filter-fields/', {
            params: { year: selectedYear.value }
          }),
          axios.get('/api/v1/crop/crops/'),
          axios.get('/api/v1/customer_bag_pit/customer_bag_pits/'),
          axios.get('/api/v1/truck/trucks/')
        ]);

        this.customerOptions = customers.data.map(item => ({ label: item.name, value: item.id }));
        this.producerOptions = producers.data.map(item => ({ label: item.name, value: item.id }));
        this.haulerOptions = haulers.data.map(item => ({ label: item.name, value: item.id }));
        this.fieldOptions = fields.data
          .map(item => ({
            label: item.producer_id?.name ? `${item.producer_id.name}-${item.name}` : item.name,
            value: item.id
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        this.filters.field_id = this.filters.field_id.filter(selectedField =>
          this.fieldOptions.some(option => option.value === selectedField.value)
        );
        this.cropOptions = crops.data.map(item => ({ label: item.crop, value: item.id }));
        this.customerBagPitFilters = customerBagPits.data.map(item => ({
          label: `${item.customer_id?.name || 'Unknown'} - ${item.name}`,
          value: item.id,
          customerId: item.customer_id?.id ?? null
        }));
        this.syncCustomerBagPitFilter();
        this.truckOptions = trucks.data
          .slice()
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(item => ({ label: item.name, value: item.id }));
      } catch (error) {
        console.error('Error fetching options:', error);
      }
    },

    async fetchTicketCrops() {
      console.log(this.filters);
      const requestSequence = ++this.ticketFetchSequence;

      try {
        const params = this.buildFilterParams();
        const response = await axios.get('/api/v1/ticket_crop/ticket-crop/', { params });

        // Changing multiple filters in quick succession creates overlapping
        // requests. Ignore stale responses so an earlier, broader query cannot
        // overwrite the final date range results.
        if (requestSequence !== this.ticketFetchSequence) {
          return;
        }

        // If using your CustomPagination, DRF returns:
        //  { count, total_pages, next, previous, results }
        this.ticketCrops = response.data.results;
        this.totalTicketCrops = response.data.count;
        // Or set totalPages from the server's "total_pages"
        this.totalPages = response.data.total_pages;
      } catch (error) {
        if (requestSequence === this.ticketFetchSequence) {
          console.error('Error fetching ticket crops:', error);
        }
      }
    },

    getExportFilename(headers) {
      const disposition = headers['content-disposition'] || headers['Content-Disposition'];
      const match = disposition?.match(/filename="?([^"]+)"?/);
      return match?.[1] || `ticket_crop_export_${selectedYear.value}.csv`;
    },

    async exportCsv() {
      this.exportingCsv = true;

      try {
        const response = await axios.get('/api/v1/ticket_crop/ticket-crop/export-csv/', {
          params: this.buildFilterParams(),
          responseType: 'blob',
        });

        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = this.getExportFilename(response.headers);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error exporting CSV:', error);
        this.$q.notify({
          color: 'red',
          message: 'Failed to export CSV.',
          icon: 'error'
        });
      } finally {
        this.exportingCsv = false;
      }
    },

    // Called when user changes page via the <q-pagination> control
    onPageChange(page) {
      this.clearSelection();
      this.currentPage = page;
      this.fetchTicketCrops();
    },

    clearSelection() {
      this.selectedRows = [];
    },

    toggleSelectAll() {
      if (this.allCurrentPageSelected) {
        this.selectedRows = this.selectedRows.filter(
          id => !this.currentPageTicketIds.includes(id)
        );
        return;
      }

      const selectedSet = new Set(this.selectedRows);
      this.currentPageTicketIds.forEach(id => selectedSet.add(id));
      this.selectedRows = Array.from(selectedSet);
    },

    onVisibleRowsChange(rows) {
      this.visibleRowIds = rows
        .map(row => row.id)
        .filter(id => id !== undefined && id !== null);
    },

    formatDisplayDate(value) {
      if (!value) {
        return '';
      }

      if (typeof value === 'string') {
        const [datePart] = value.split('T');
        const [year, month, day] = datePart.split('-').map(Number);

        if (year && month && day) {
          return new Date(year, month - 1, day).toLocaleDateString();
        }
      }

      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
    },

    formatFilterDateTime(date, boundary) {
      return `${date}${boundary === 'end' ? 'T23:59:59Z' : 'T00:00:00Z'}`;
    },

    syncCustomerBagPitFilter() {
      if (!this.filters.bag_pit_id) {
        return;
      }

      const selectedBagPitValue = this.filters.bag_pit_id.value;
      const selectedBagPitStillAvailable = this.customerBagPitOptions.some(
        option => option.value === selectedBagPitValue
      );

      if (!selectedBagPitStillAvailable) {
        this.filters.bag_pit_id = null;
      }
    },

    openModal() {
      if (this.bulkEditSelectedRows.length < 1) {
        return;
      }

      const selectedRowFirst = this.ticketCrops.find(
        crop => crop.id === this.bulkEditSelectedRows[0]
      );

      if (!selectedRowFirst) {
        console.error('Selected row not found in ticketCrops');
        return;
      }

      // Populate editForm from the first selected row
      this.editForm = {
        date: this.formatDateForInput(selectedRowFirst.date),
        at_moisture: selectedRowFirst.at_moisture ?? null,
        moisture: selectedRowFirst.moisture ?? null,
        customer_rate: selectedRowFirst.customer_rate ?? null,
        producer_rate: selectedRowFirst.producer_rate ?? null,
        haul_rate: selectedRowFirst.haul_rate ?? null,
        haul_mile_rate: selectedRowFirst.haul_mile_rate ?? null,
        haul_mile: selectedRowFirst.haul_mile ?? null,
        min_mile: selectedRowFirst.min_mile ?? null,
        bag_pit_id: selectedRowFirst.bag_pit_id?.id ?? null,
        truck_id: selectedRowFirst.truck_id?.id ?? null,
        customer_id: selectedRowFirst.customer_id?.id ?? null,
        producer_id: selectedRowFirst.producer_id?.id ?? null,
        ticket_number: selectedRowFirst.ticket_number ?? null
      };
      this.editFieldSelection = {
        date: false,
        at_moisture: false,
        moisture: false,
        customer_rate: false,
        producer_rate: false,
        haul_rate: false,
        haul_mile_rate: false,
        haul_mile: false,
        min_mile: false,
        bag_pit_id: false,
        truck_id: false,
        customer_id: false,
        producer_id: false,
        ticket_number: false
      };
      this.showBulkEditDatePicker = false;
      this.showModal = true;
    },

    validateNumeric(value) {
      return (value !== null && value !== '' && !isNaN(value))
        || 'This field must be a numeric value';
    },

    validateOptionalNumeric(fieldKey, value) {
      if (!this.editFieldSelection[fieldKey]) {
        return true;
      }

      return this.validateNumeric(value);
    },

    validateOptionalText(fieldKey, value) {
      if (!this.editFieldSelection[fieldKey]) {
        return true;
      }

      return String(value ?? '').trim().length > 0
        || 'Enter a value';
    },

    isBulkEditSelection(fieldKey) {
      return ['truck_id', 'bag_pit_id', 'customer_id', 'producer_id'].includes(fieldKey);
    },

    bulkEditOptions(fieldKey) {
      const options = {
        truck_id: this.truckOptions,
        bag_pit_id: this.customerBagPitFilters,
        customer_id: this.customerOptions,
        producer_id: this.producerOptions
      };

      return options[fieldKey] || [];
    },

    validateOptionalSelection(fieldKey, value) {
      if (!this.editFieldSelection[fieldKey]) {
        return true;
      }

      return value !== null && value !== undefined && value !== ''
        || 'Select a value';
    },

    validateOptionalDate(value) {
      if (!this.editFieldSelection.date) {
        return true;
      }

      if (!value) {
        return 'Select a date';
      }

      const [year] = String(value).split('-').map(Number);
      return year === selectedYear.value
        || `Date must be within ${selectedYear.value}`;
    },

    onEditFieldSelectionChange(fieldKey, selected) {
      if (fieldKey !== 'date') {
        return;
      }

      this.showBulkEditDatePicker = selected;
    },

    formatDateForInput(value) {
      if (!value) {
        return null;
      }

      if (typeof value === 'string') {
        return value.split('T')[0];
      }

      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) {
        return null;
      }

      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, '0');
      const day = String(parsed.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    formatBulkUpdateDate(date) {
      return `${date}T00:00:00Z`;
    },

    async submitEditForm() {
      this.$refs.editFormRef.validate().then(async (success) => {
        if (success) {
          try {
            const payload = { ids: this.bulkEditSelectedRows };

            this.editFieldConfigs.forEach(field => {
              if (this.editFieldSelection[field.key]) {
                payload[field.key] = field.key === 'date'
                  ? this.formatBulkUpdateDate(this.editForm.date)
                  : this.editForm[field.key];
              }
            });

            if (Object.keys(payload).length === 1) {
              this.$q.notify({
                color: 'red',
                message: 'Select at least one field to update.',
                icon: 'error'
              });
              return;
            }

            await axios.put(
              '/api/v1/ticket_crop/ticket-crop/bulk-update/',
              payload
            );

            this.$q.notify({
              color: 'green',
              message: 'Crop ticket updated successfully!',
              icon: 'check'
            });

            this.showModal = false;
            this.fetchTicketCrops();
          } catch (error) {
            console.error('Error submitting form:', error);
            this.$q.notify({
              color: 'red',
              message: 'Failed to update crop tickets.',
              icon: 'error'
            });
          }
        }
      });
    },

    openDeleteModal() {
      if (this.selectedRows.length < 1) {
        this.$q.notify({
          color: 'red',
          message: 'No tickets selected to delete.',
          icon: 'error'
        });
        return;
      }
      this.deleteModalVisible = true;
    },

    async confirmBulkDelete() {
      try {
        const response = await axios.delete(
          '/api/v1/ticket_crop/ticket-crop/bulk-delete/',
          { data: { ids: this.selectedRows } }
        );

        this.$q.notify({
          color: 'green',
          message: response.data.message,
          icon: 'check'
        });

        // Refresh data, close modal
        this.fetchTicketCrops();
        this.deleteModalVisible = false;
        this.selectedRows = [];
      } catch (error) {
        console.error('Error during bulk delete:', error);
        this.$q.notify({
          color: 'red',
          message: 'Failed to delete crop tickets.',
          icon: 'error'
        });
      }
    },

    cancelBulkDelete() {
      this.deleteModalVisible = false;
    },

    clearDateStart() {
      this.filters.dateStart = null;
    },

    clearDateEnd() {
      this.filters.dateEnd = null;
    },

    clearDateRange() {
      this.clearDateStart();
      this.clearDateEnd();
    },

    clearFilters() {
      this.filters = {
        ticketNumber: null,
        dateStart: null,
        dateEnd: null,
        customer_id: null,
        producer_id: null,
        hauler_id: null,
        field_id: [],
        crop_id: null,
        bag_pit_id: null,
        job_id: this.initialJobId,
      };
      // Reset page to 1 on filter clear
      this.currentPage = 1;
    },
  }
};
</script>

<style scoped>
/* Add specific styles for the TicketCropTable here if needed */
.inline-tooltip-target {
  display: inline-block;
}
</style>
